import {
  CREATING_SKILL_FAILED,
  CREATING_SKILL_REQUEST,
  CREATING_SKILL_SUCCEEDED,
  DELETING_SKILL_FAILED,
  DELETING_SKILL_REQUEST,
  DELETING_SKILL_SUCCEEDED,
  FETCH_ASSESSMENT_CONFIGURATIONS_REQUEST,
  FETCHING_ASSESSMENT_CONFIGURATIONS_FAILED,
  FETCHING_ASSESSMENT_CONFIGURATIONS_SUCCEEDED,
  FETCHING_SKILLS_FAILED,
  FETCHING_SKILLS_REQUEST,
  FETCHING_SKILLS_SUCCEEDED,
  INVALIDATE_CREATE_SKILL,
  INVALIDATE_DELETE_SKILL,
  INVALIDATE_SKILLS,
  SEARCHING_SKILLS_FAILED,
  SEARCHING_SKILLS_REQUEST,
  SEARCHING_SKILLS_SUCCEEDED,
} from "src/redux/actions/actionTypes";
import _ from "lodash";
import { Skill } from "../../../types/coaching/courses";
import { INVALIDATE_UPDATE_SKILL, UPDATING_SKILL_FAILED, UPDATING_SKILL_REQUEST, UPDATING_SKILL_SUCCEEDED } from "../../actions/actionTypes";
import { CreateSkillResponse, DeleteSkillResponse, UpdateSkillResponse } from "../../../types/coaching/skills";
import { orderBy } from "natural-orderby";
import { AssessmentConfiguration, FetchDefaultAssessmentConfigurationErrorResponse, FetchDefaultAssessmentConfigurationResponse } from "src/types/coaching/assessmentConfiguration";


const updateSkillInternalId = (skill: Skill): Skill => {
  if (!skill || !skill.id) {
    return skill;
  }

  const updated = { ...skill };

  if (skill.id && !skill.uuid) {
    updated.uuid = skill.id; // Ensure uuid is set if id exists
  }

  if (_.size(skill.children) > 0) {
    updated.children = skill.children.map((child: any) => {
      if (child.id && !child.uuid) {
        child.uuid = child.id; // Ensure uuid is set for children as well
      }
      return child;
    });
  }

  return updated;
}

interface ScorecardsState {
  loading: boolean;
  loaded: boolean;
  items: AssessmentConfiguration[];
  error: any;
}


interface SettingsState {
  skills: SkillsState;
  scorecards: ScorecardsState;
}


interface SkillCreationState {
  creating: boolean,
  created: boolean,
  type: string | null,
  item: Skill | null,
  error: any
}

interface SkillUpdateState {
  id: string,
  updating: boolean,
  updated: boolean,
  item: Skill | null,
  error: any
}

interface SkillDeleteState {
  id: string,
  deleting: boolean,
  deleted: boolean,
  error: any
}

interface SkillsState {
  loading: boolean;
  loaded: boolean;
  items: any[];
  searchResults: any[];
  pagination: {
    currentPage: number;
    totalPages: number;
    recordsPerPage: number;
    totalRecordsCount: number;
    hasMoreItems: boolean;
  };
  error: any;
  creation: SkillCreationState | null;
  update: SkillUpdateState | null;
  deletion: SkillDeleteState | null;
}

const initialSkillsState: SkillsState = {
  loading: false,
  loaded: false,
  items: [],
  searchResults: [],
  pagination: {
    currentPage: 0,
    totalPages: 0,
    recordsPerPage: 0,
    totalRecordsCount: 0,
    hasMoreItems: false,
  },
  error: null,
  creation: null,
  update: null,
  deletion: null,
};

const initialScorecardsState: ScorecardsState = {
  loading: false,
  loaded: false,
  items: [],
  error: null,
};

const initialSettingsState: SettingsState = {
  skills: initialSkillsState,
  scorecards: initialScorecardsState,
};

export function settingsReducer(state = initialSettingsState, action: any) {
  switch (action.type) {

    case FETCHING_SKILLS_REQUEST: {
      return {
        ...state,
        skills: {
          ...state.skills,
          loading: true,
          loaded: false,
          error: null,
        }
      };
    }

    case FETCHING_SKILLS_SUCCEEDED: {

      const { response } = action.payload as any;
      const existingItems = state.skills.items || [];

      let records = response.records;
      if (response.currentPage > 0) {
        records = [...existingItems, ...records];
      }

      records = _.uniqBy(records, "id");
      records = orderBy(records, [skill => (skill as any).name], "asc");
      records = records.map((item: any) => {
        const updated = updateSkillInternalId(item);
        return updated;
      });

      return {
        ...state,
        skills: {
          ...state.skills,
          loading: false,
          loaded: true,
          items: records,
          pagination: {
            currentPage: response.currentPage,
            totalPages: response.totalPages,
            recordsPerPage: response.recordsPerPage,
            totalRecordsCount: response.totalRecordsCount,
            hasMoreItems: response.currentPage < response.totalPages - 1,
          },
          error: null,
        }
      };
    }

    case FETCHING_SKILLS_FAILED: {

      return {
        ...state,
        skills: {
          ...state.skills,
          loading: false,
          loaded: false,
          error: action.payload.error,
        }
      };
    }

    case SEARCHING_SKILLS_REQUEST: {

      return {
        ...state,
        skills: {
          ...state.skills,
          loading: true,
          loaded: false,
          error: null,
        }
      };
    }

    case SEARCHING_SKILLS_SUCCEEDED: {

      const { response } = action.payload as any;
      const existingItems = state.skills.searchResults || [];

      let records = response.records;
      if (response.currentPage > 0) {
        records = [...existingItems, ...records];
      }

      records = _.uniqBy(records, "id");
      records = orderBy(records, [skill => (skill as any).name], "asc");

      return {
        ...state,
        skills: {
          ...state.skills,
          loading: false,
          loaded: true,
          searchResults: records,
          pagination: {
            currentPage: response.currentPage,
            totalPages: response.totalPages,
            recordsPerPage: response.recordsPerPage,
            totalRecordsCount: response.totalRecordsCount,
            hasMoreItems: response.currentPage < response.totalPages - 1,
          },
          error: null
        }
      }
    }

    case SEARCHING_SKILLS_FAILED: {

      return {
        ...state,
        skills: {
          ...state.skills,
          loading: false,
          loaded: false,
          error: action.payload.error,
        }
      }
    }

    case INVALIDATE_SKILLS: {
      return {
        ...state,
        skills: {
          ...state.skills,
          loading: false,
          loaded: false,
          items: [],
          searchResults: [],
          pagination: {
            currentPage: 0,
            totalPages: 0,
            recordsPerPage: 0,
            totalRecordsCount: 0,
            hasMoreItems: false,
          },
          error: null,
        }
      };
    }

    case CREATING_SKILL_REQUEST: {
      return {
        ...state,
        skills: {
          ...state.skills,
          creation: {
            creating: true,
            created: false,
            item: null,
            error: null
          }
        }
      };
    }

    case CREATING_SKILL_SUCCEEDED: {
      const { skill } = action.payload as CreateSkillResponse;

      if (!state.skills.creation) {
        return state;
      }

      const updatedSkill = updateSkillInternalId(skill);
      let records = [...state.skills.items, updatedSkill]
      records = orderBy(records, [skill => (skill as any).name], "asc");

      return {
        ...state,
        skills: {
          ...state.skills,
          creation: {
            creating: false,
            created: true,
            item: skill,
            error: null
          },
          items: records
        }
      };
    }

    case CREATING_SKILL_FAILED: {
      if (!state.skills.creation) {
        return state;
      }

      return {
        ...state,
        skills: {
          ...state.skills,
          creation: {
            creating: false,
            created: false,
            item: null,
            error: action.payload.error
          }
        }
      };
    }

    case INVALIDATE_CREATE_SKILL: {
      return {
        ...state,
        skills: {
          ...state.skills,
          creation: null
        }
      };

    }

    case UPDATING_SKILL_REQUEST: {
      const { id } = action.payload as any;
      return {
        ...state,
        skills: {
          ...state.skills,
          update: {
            id,
            updating: true,
            updated: false,
            item: null,
            error: null
          }
        }
      };
    }

    case UPDATING_SKILL_SUCCEEDED: {
      const { skill } = action.payload as UpdateSkillResponse;

      if (!state.skills.update) {
        return state;
      }

      let updatedItems = state.skills.items.map((item) => {
        if (item.id === skill.id) {
          const updatedSkill = updateSkillInternalId(skill);
          return { ...item, ...updatedSkill };
        }
        return item;
      });

      updatedItems = orderBy(updatedItems, [skill => (skill as any).name], "asc");

      return {
        ...state,
        skills: {
          ...state.skills,
          update: {
            id: skill.id,
            updating: false,
            updated: true,
            item: skill,
            error: null
          },
          items: updatedItems
        }
      };
    }

    case UPDATING_SKILL_FAILED: {
      if (!state.skills.update) {
        return state;
      }

      return {
        ...state,
        skills: {
          ...state.skills,
          update: {
            id: action.payload.id,
            updating: false,
            updated: false,
            item: null,
            error: action.payload.error
          }
        }
      };
    }

    case INVALIDATE_UPDATE_SKILL: {
      return {
        ...state,
        skills: {
          ...state.skills,
          update: null
        }
      };
    }

    case DELETING_SKILL_REQUEST: {
      const { id } = action.payload as any;
      return {
        ...state,
        skills: {
          ...state.skills,
          deletion: {
            id,
            deleting: true,
            deleted: false,
            item: null,
            error: null
          }
        }
      };
    }

    case DELETING_SKILL_SUCCEEDED: {
      const { id } = action.payload as DeleteSkillResponse;

      if (!state.skills.deletion) {
        return state;
      }

      const updatedItems = state.skills.items.filter((item) => {
        return item.id !== id;
      });

      return {
        ...state,
        skills: {
          ...state.skills,
          deletion: {
            id: id,
            deleting: false,
            deleted: true,
            error: null
          },
          items: updatedItems
        }
      };
    }

    case DELETING_SKILL_FAILED: {
      if (!state.skills.deletion) {
        return state;
      }

      return {
        ...state,
        skills: {
          ...state.skills,
          deletion: {
            id: action.payload.id,
            deleting: false,
            deleted: false,
            error: action.payload.error
          }
        }
      };
    }

    case INVALIDATE_DELETE_SKILL: {
      return {
        ...state,
        skills: {
          ...state.skills,
          deletion: null
        }
      };
    }

    case FETCH_ASSESSMENT_CONFIGURATIONS_REQUEST: {
      return {
        ...state,
        scorecards: {
          ...state.scorecards,
            loading: true,
            loaded: false,
            error: null
        }
      }
    }

    case FETCHING_ASSESSMENT_CONFIGURATIONS_SUCCEEDED: {
      const { response } = action.payload as FetchDefaultAssessmentConfigurationResponse;

      return {
        ...state,
        scorecards: {
          ...state.scorecards,
          loading: false,
          loaded: true,
          items: response,
          error: null
        }
      }
    }

    case FETCHING_ASSESSMENT_CONFIGURATIONS_FAILED: {
      const { error } = action.payload as FetchDefaultAssessmentConfigurationErrorResponse;

      return {
        ...state,
        scorecards: {
          ...state.scorecards,
          loading: false,
          loaded: false,
          error: error
        }
      }
    }

    default:
      return state;
  }
}
